import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Check } from "lucide-react";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG =
    "https://images.unsplash.com/photo-1557143930-d4e7a86a194f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800";
const STEEL_IMG =
    "https://images.unsplash.com/photo-1622109912940-2bddde35274d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const WORK_IMG =
    "https://images.unsplash.com/photo-1576666735065-b24beb27b939?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
    const { t, dir } = useLang();

    const stats = [
        { l: t.home.stat1Label, v: t.home.stat1Value },
        { l: t.home.stat2Label, v: t.home.stat2Value },
        { l: t.home.stat3Label, v: t.home.stat3Value },
    ];

    return (
        <main data-testid="page-home" className="bg-white">
            <SEO pathKey="home" path="/" />
            {/* HERO */}
            <section className="relative isolate pt-28 lg:pt-36 pb-20 lg:pb-28 overflow-hidden">
                <div className="absolute inset-0 -z-10 blueprint-bg opacity-50" />
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end">
                    <div className="lg:col-span-7">
                        <Reveal delay={0}>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-steel-600 flex items-center gap-3">
                                <span className="inline-block w-8 h-px bg-navy" />
                                {t.home.kicker}
                            </div>
                        </Reveal>
                        <Reveal delay={80}>
                            <h1 className="font-display mt-6 text-[14vw] sm:text-7xl lg:text-[7.5rem] leading-[0.88] tracking-tighter text-navy uppercase">
                                <span className="block">{t.home.h1Line1}</span>
                                <span className="block">{t.home.h1Line2}</span>
                                <span className="block text-steel-400">{t.home.h1Line3}</span>
                                <span className="block">{t.home.h1Line4}</span>
                            </h1>
                        </Reveal>
                        <Reveal delay={180}>
                            <p className="mt-8 max-w-xl text-steel-600 text-base lg:text-lg leading-relaxed">
                                {t.home.sub}
                            </p>
                        </Reveal>
                        <Reveal delay={260}>
                            <div className="mt-10 flex flex-wrap gap-3">
                                <Link
                                    to="/contact"
                                    data-testid="hero-cta-consult"
                                    className="group inline-flex items-center gap-3 bg-navy text-white px-7 py-4 font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-navy-light transition-colors"
                                >
                                    {t.common.consult}
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1" />
                                </Link>
                                <Link
                                    to="/services"
                                    data-testid="hero-cta-services"
                                    className="inline-flex items-center gap-3 border border-navy text-navy px-7 py-4 font-mono-tech text-[12px] uppercase tracking-[0.2em] hover:bg-navy hover:text-white transition-colors"
                                >
                                    {t.nav.services}
                                </Link>
                            </div>
                        </Reveal>
                    </div>

                    <Reveal delay={120} className="lg:col-span-5">
                        <div className="relative aspect-[4/5] lg:aspect-[3/4] overflow-hidden border border-steel-100">
                            <img
                                src={HERO_IMG}
                                alt="European industrial architecture"
                                className="absolute inset-0 w-full h-full object-cover grayscale contrast-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-br from-navy/30 via-transparent to-navy-dark/40 mix-blend-multiply" />
                            <div className="absolute bottom-0 inset-x-0 p-5 bg-navy text-white">
                                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-200">
                                    {t.common.since}
                                </div>
                                <div className="font-display text-2xl tracking-tight mt-1">
                                    Craiova · RO → EU
                                </div>
                            </div>
                        </div>
                    </Reveal>
                </div>

                {/* Stat strip */}
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 mt-16 lg:mt-24">
                    <div className="grid grid-cols-1 md:grid-cols-3 border-t border-steel-100">
                        {stats.map((s, i) => (
                            <Reveal
                                key={s.l}
                                delay={i * 80}
                                className={`p-6 md:p-8 border-b md:border-b-0 border-steel-100 ${
                                    i < stats.length - 1
                                        ? dir === "rtl"
                                            ? "md:border-s md:border-steel-100"
                                            : "md:border-e md:border-steel-100"
                                        : ""
                                }`}
                            >
                                <div className="font-mono-tech text-[11px] uppercase tracking-[0.25em] text-steel-600">
                                    {s.l}
                                </div>
                                <div className="mt-3 font-display text-3xl lg:text-4xl tracking-tight text-navy">
                                    {s.v}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 mt-10 flex items-center gap-3 text-steel-400">
                    <ArrowDown className="h-4 w-4" />
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.3em]">
                        {t.common.scroll}
                    </span>
                </div>
            </section>

            {/* VALUE PROP */}
            <section className="relative bg-steel-50 border-y border-steel-100 py-24 lg:py-32">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-navy">
                                {t.home.valueProp.overline}
                            </div>
                            <h2 className="font-display mt-5 text-4xl lg:text-5xl uppercase tracking-tighter text-navy leading-[0.95]">
                                {t.home.valueProp.title}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7">
                        <Reveal delay={120}>
                            <p className="text-lg text-steel-800 leading-relaxed">
                                {t.home.valueProp.body}
                            </p>
                        </Reveal>
                        <ul className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                            {t.home.valueProp.points.map((p, i) => (
                                <Reveal key={p} delay={150 + i * 60}>
                                    <li className="flex items-start gap-3 text-steel-800">
                                        <span className="mt-1 inline-flex h-5 w-5 items-center justify-center bg-navy text-white">
                                            <Check className="h-3 w-3" strokeWidth={3} />
                                        </span>
                                        <span className="leading-relaxed">{p}</span>
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* SERVICES */}
            <section className="py-24 lg:py-32">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
                        <Reveal>
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.3em] text-navy">
                                {t.home.services.overline}
                            </div>
                            <h2 className="font-display mt-4 text-4xl lg:text-6xl uppercase tracking-tighter text-navy leading-[0.95] max-w-3xl">
                                {t.home.services.title}
                            </h2>
                        </Reveal>
                        <Reveal delay={120}>
                            <Link
                                to="/services"
                                data-testid="home-services-link"
                                className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.25em] text-navy link-underline"
                            >
                                {t.nav.services}
                                <ArrowRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
                            </Link>
                        </Reveal>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 border-t border-s border-steel-100">
                        {t.home.services.items.map((it, i) => (
                            <Reveal
                                key={it.code}
                                delay={i * 80}
                                className="group p-8 lg:p-10 border-e border-b border-steel-100 bg-white hover:bg-navy hover:text-white transition-colors duration-200"
                            >
                                <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-steel-400 group-hover:text-steel-200">
                                    {it.code}
                                </div>
                                <h3 className="font-display mt-4 text-2xl lg:text-3xl uppercase tracking-tight text-navy group-hover:text-white">
                                    {it.title}
                                </h3>
                                <p className="mt-4 text-steel-600 group-hover:text-steel-200 leading-relaxed">
                                    {it.body}
                                </p>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Marquee tape */}
            <section className="bg-navy text-white py-8 overflow-hidden border-y border-navy-light/30">
                <div className="marquee-track gap-12 whitespace-nowrap">
                    {[...t.home.tape, ...t.home.tape, ...t.home.tape].map((w, i) => (
                        <span
                            key={i}
                            className="font-display text-3xl lg:text-5xl tracking-tight uppercase flex items-center gap-12"
                        >
                            {w}
                            <span className="text-steel-400 text-2xl">/</span>
                        </span>
                    ))}
                </div>
            </section>

            {/* Imagery split */}
            <section className="py-24 lg:py-32">
                <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 gap-1">
                    <Reveal className="relative aspect-[4/3] overflow-hidden">
                        <img src={STEEL_IMG} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                        <div className="absolute inset-0 bg-navy/10" />
                    </Reveal>
                    <Reveal delay={120} className="relative aspect-[4/3] overflow-hidden">
                        <img src={WORK_IMG} alt="" className="absolute inset-0 w-full h-full object-cover grayscale" />
                        <div className="absolute inset-0 bg-navy/10" />
                    </Reveal>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-navy-dark text-white relative overflow-hidden grain">
                <div className="absolute inset-0 blueprint-bg opacity-10" />
                <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10 py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-8">
                        <Reveal>
                            <h2 className="font-display text-4xl lg:text-6xl uppercase tracking-tighter leading-[0.95]">
                                {t.home.cta.title}
                            </h2>
                            <p className="mt-6 max-w-2xl text-steel-200 text-lg leading-relaxed">
                                {t.home.cta.body}
                            </p>
                        </Reveal>
                    </div>
                    <Reveal delay={120} className="lg:col-span-4 lg:text-end">
                        <Link
                            to="/contact"
                            data-testid="home-bottom-cta"
                            className="inline-flex items-center gap-3 bg-white text-navy px-8 py-5 font-mono-tech text-[12px] uppercase tracking-[0.25em] hover:bg-steel-100 transition-colors"
                        >
                            {t.home.cta.button}
                            <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
